/**
 * InputManager — Unified input system supporting keyboard, gamepad, and touch.
 * Provides per-player state via InputManager.getState(playerId).
 * Supports 4 players (indices 0–3).
 *
 * @module InputManager
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Gamepad button indices (standard mapping) */
const GP = {
  A: 0,          // Cross
  B: 1,          // Circle
  X: 2,          // Square
  Y: 3,          // Triangle
  LB: 4,
  RB: 5,
  LT: 6,
  RT: 7,
  SELECT: 8,     // Back / Select
  START: 9,
  L3: 10,
  R3: 11,
  DPAD_UP: 12,
  DPAD_DOWN: 13,
  DPAD_LEFT: 14,
  DPAD_RIGHT: 15,
};

const AXIS_DEADZONE = 0.15;

// ─── State factory ────────────────────────────────────────────────────────────

/**
 * Create a fresh per-player input state object.
 * @returns {object}
 */
function createPlayerState() {
  return {
    up: false,
    down: false,
    left: false,
    right: false,
    confirm: false,
    cancel: false,
    inventory: false,
    questLog: false,
    charSheet: false,
    compendium: false,
    map: false,
    menuOpen: false,
    submenu: false,
    debug: false,
    skipTurn: false,
    undoMove: false,
    quickSave: false,
    quickLoad: false,
    moveAxis: { x: 0, y: 0 },
    aimAxis:  { x: 0, y: 0 },
    anyKey: false,
  };
}

/**
 * Deep clone a player state (used for "previous frame" comparisons).
 * @param {object} state
 * @returns {object}
 */
function cloneState(state) {
  return {
    ...state,
    moveAxis: { ...state.moveAxis },
    aimAxis:  { ...state.aimAxis },
  };
}

// ─── InputManager ────────────────────────────────────────────────────────────

const InputManager = {
  /** @type {Array<object>} Per-player state — index 0–3 */
  _states: [
    createPlayerState(),
    createPlayerState(),
    createPlayerState(),
    createPlayerState(),
  ],

  /** @type {Array<object>} Previous frame states for edge detection */
  _prevStates: [
    createPlayerState(),
    createPlayerState(),
    createPlayerState(),
    createPlayerState(),
  ],

  /**
   * Player source assignments.
   * Each entry: { type: 'keyboard_wasd'|'keyboard_arrows'|'gamepad', gamepadIndex }
   */
  _playerSources: [
    { type: 'keyboard_wasd',   gamepadIndex: 0 },
    { type: 'keyboard_arrows', gamepadIndex: 1 },
    { type: 'gamepad',         gamepadIndex: 2 },
    { type: 'gamepad',         gamepadIndex: 3 },
  ],

  /** @type {Set<string>} Currently held keyboard keys */
  _keysDown: new Set(),

  /** @type {Map<string, boolean>} Simulated actions (Playwright testing) */
  _simulated: new Map(),

  /** @type {boolean} Whether L3 is held (for L3+R3 debug combo) */
  _gp0L3: false,

  /** @type {number|null} RAF handle */
  _rafHandle: null,

  /** @type {{x: number, y: number}} Last known mouse/touch aim position in game coords */
  _mouseAim: { x: 0, y: 0 },

  /** @type {{x: number, y: number}|null} Screen coords where touch drag started */
  _touchOrigin: null,

  /** @type {boolean} Whether the current touch has moved beyond the dead zone */
  _touchMoveActive: false,

  /**
   * Initialize the input manager. Sets up event listeners and starts polling loop.
   */
  init() {
    this._bindKeyboard();
    this._bindTouch();
    this._startGamepadPoll();
    DebugLog.info('[input] InputManager initialized');
  },

  // ─── Keyboard ────────────────────────────────────────────────────────────

  _bindKeyboard() {
    window.addEventListener('keydown', (e) => this._onKeyDown(e), { passive: false });
    window.addEventListener('keyup',   (e) => this._onKeyUp(e),   { passive: false });
  },

  _onKeyDown(e) {
    const key = e.code || e.key;
    if (this._keysDown.has(key)) return; // already held
    this._keysDown.add(key);
    this._applyKeyboardState(key, true, e);
  },

  _onKeyUp(e) {
    const key = e.code || e.key;
    this._keysDown.delete(key);
    this._applyKeyboardState(key, false, e);
  },

  /**
   * Map a keyboard key to the appropriate player state fields.
   * @param {string} key — e.code value
   * @param {boolean} pressed
   * @param {KeyboardEvent} e
   */
  _applyKeyboardState(key, pressed, e) {
    const p1 = this._states[0];
    const p2 = this._states[1];

    // Debug console — tilde / backtick (code 'Backquote', key '`' or '~')
    if (key === 'Backquote' || e.key === '`' || e.key === '~') {
      if (pressed) {
        p1.debug = true;
        EventBus.emit('input:debug', { playerId: 0 });
      } else {
        p1.debug = false;
      }
      return;
    }

    // ── P1: WASD ──────────────────────────────────────────
    switch (key) {
      case 'KeyW': case 'ArrowUp_P1': // handled below
        if (key === 'KeyW') {
          p1.up = pressed;
          p1.moveAxis.y = pressed ? -1 : (this._keysDown.has('KeyS') ? 1 : 0);
          p1.anyKey = pressed;
        }
        break;
      case 'KeyS':
        p1.down = pressed;
        p1.moveAxis.y = pressed ? 1 : (this._keysDown.has('KeyW') ? -1 : 0);
        p1.anyKey = pressed;
        break;
      case 'KeyA':
        p1.left = pressed;
        p1.moveAxis.x = pressed ? -1 : (this._keysDown.has('KeyD') ? 1 : 0);
        p1.anyKey = pressed;
        break;
      case 'KeyD':
        p1.right = pressed;
        p1.moveAxis.x = pressed ? 1 : (this._keysDown.has('KeyA') ? -1 : 0);
        p1.anyKey = pressed;
        break;
      case 'Enter': case 'KeyE':
        p1.confirm = pressed;
        p1.anyKey = pressed;
        break;
      case 'Escape':
        p1.cancel    = pressed;
        p1.menuOpen  = pressed;
        p1.anyKey    = pressed;
        break;
      case 'KeyI':
        p1.inventory = pressed;
        p1.anyKey = pressed;
        break;
      case 'KeyJ':
        p1.questLog = pressed;
        p1.anyKey = pressed;
        break;
      case 'KeyC':
        p1.charSheet = pressed;
        p1.anyKey = pressed;
        break;
      case 'KeyK':
        p1.compendium = pressed;
        p1.anyKey = pressed;
        break;
      case 'KeyM':
        p1.map = pressed;
        p1.anyKey = pressed;
        break;
      case 'Tab':
        p1.submenu = pressed;
        p1.anyKey = pressed;
        // Prevent focus cycling out of game
        if (pressed) e.preventDefault?.();
        break;
      case 'Space':
        p1.skipTurn = pressed;
        p1.anyKey = pressed;
        break;
      case 'KeyZ':
        p1.undoMove = pressed;
        p1.anyKey = pressed;
        break;
      case 'F5':
        if (pressed) { p1.quickSave = true; p1.anyKey = true; e.preventDefault?.(); }
        else           p1.quickSave = false;
        break;
      case 'F9':
        if (pressed) { p1.quickLoad = true; p1.anyKey = true; e.preventDefault?.(); }
        else           p1.quickLoad = false;
        break;

      // ── P2: Arrow keys ───────────────────────────────────
      case 'ArrowUp':
        p2.up = pressed;
        p2.moveAxis.y = pressed ? -1 : (this._keysDown.has('ArrowDown') ? 1 : 0);
        p2.anyKey = pressed;
        break;
      case 'ArrowDown':
        p2.down = pressed;
        p2.moveAxis.y = pressed ? 1 : (this._keysDown.has('ArrowUp') ? -1 : 0);
        p2.anyKey = pressed;
        break;
      case 'ArrowLeft':
        p2.left = pressed;
        p2.moveAxis.x = pressed ? -1 : (this._keysDown.has('ArrowRight') ? 1 : 0);
        p2.anyKey = pressed;
        break;
      case 'ArrowRight':
        p2.right = pressed;
        p2.moveAxis.x = pressed ? 1 : (this._keysDown.has('ArrowLeft') ? -1 : 0);
        p2.anyKey = pressed;
        break;
      case 'Numpad0':
        p2.confirm = pressed;
        p2.anyKey = pressed;
        break;

      default:
        break;
    }
  },

  // ─── Touch ───────────────────────────────────────────────────────────────

  _bindTouch() {
    const canvas = document.getElementById('game-canvas');
    if (!canvas) return;

    canvas.addEventListener('touchstart',  (e) => this._onTouchStart(e),  { passive: true });
    canvas.addEventListener('touchmove',   (e) => this._onTouchMove(e),   { passive: true });
    canvas.addEventListener('touchend',    (e) => this._onTouchEnd(e),    { passive: true });
    canvas.addEventListener('touchcancel', (e) => this._onTouchEnd(e),    { passive: true });

    // Mouse aim for P1 (desktop)
    canvas.addEventListener('mousemove', (e) => {
      const pos = this._screenToGame(e.clientX, e.clientY);
      this._mouseAim.x = pos.x;
      this._mouseAim.y = pos.y;
      this._states[0].aimAxis.x = pos.x;
      this._states[0].aimAxis.y = pos.y;
    }, { passive: true });

    canvas.addEventListener('click', () => {
      this._states[0].confirm = true;
      this._states[0].anyKey = true;
      // Reset confirm after one frame (handled in next poll)
      requestAnimationFrame(() => {
        this._states[0].confirm = false;
      });
    }, { passive: true });
  },

  _onTouchStart(e) {
    if (e.touches.length === 0) return;
    const touch = e.touches[0];
    // Record origin for drag-movement
    this._touchOrigin = { x: touch.clientX, y: touch.clientY };
    this._touchMoveActive = false;
    // Aim and confirm
    const pos = this._screenToGame(touch.clientX, touch.clientY);
    this._states[0].aimAxis.x = pos.x;
    this._states[0].aimAxis.y = pos.y;
    this._states[0].confirm = true;
    this._states[0].anyKey  = true;
  },

  _onTouchMove(e) {
    if (e.touches.length === 0) return;
    const touch = e.touches[0];
    const pos = this._screenToGame(touch.clientX, touch.clientY);
    this._states[0].aimAxis.x = pos.x;
    this._states[0].aimAxis.y = pos.y;

    // Drag movement: convert touch drag delta to moveAxis
    if (this._touchOrigin) {
      const dx = touch.clientX - this._touchOrigin.x;
      const dy = touch.clientY - this._touchOrigin.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const DEAD_ZONE = 12; // pixels before movement starts
      const MAX_DIST  = 60; // pixels for full speed

      if (dist > DEAD_ZONE) {
        this._touchMoveActive = true;
        const speed = Math.min((dist - DEAD_ZONE) / (MAX_DIST - DEAD_ZONE), 1);
        this._states[0].moveAxis.x = (dx / dist) * speed;
        this._states[0].moveAxis.y = (dy / dist) * speed;
      } else {
        this._states[0].moveAxis.x = 0;
        this._states[0].moveAxis.y = 0;
      }
    }
  },

  _onTouchEnd(_e) {
    this._states[0].confirm = false;
    this._states[0].moveAxis.x = 0;
    this._states[0].moveAxis.y = 0;
    this._touchOrigin = null;
    this._touchMoveActive = false;
  },

  /**
   * Convert screen coordinates to game (renderer) coordinates.
   * @param {number} screenX
   * @param {number} screenY
   * @returns {{x: number, y: number}}
   */
  _screenToGame(screenX, screenY) {
    const canvas = document.getElementById('game-canvas');
    if (!canvas) return { x: screenX, y: screenY };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / (rect.width  || 1);
    const scaleY = canvas.height / (rect.height || 1);
    return {
      x: (screenX - rect.left) * scaleX,
      y: (screenY - rect.top)  * scaleY,
    };
  },

  // ─── Gamepad polling ─────────────────────────────────────────────────────

  _startGamepadPoll() {
    const poll = () => {
      this._pollGamepads();
      this._applySimulated();
      this._rafHandle = requestAnimationFrame(poll);
    };
    this._rafHandle = requestAnimationFrame(poll);
  },

  _pollGamepads() {
    const gamepads = navigator.getGamepads?.() ?? [];

    for (let playerId = 0; playerId < 4; playerId++) {
      const source = this._playerSources[playerId];
      if (source.type === 'keyboard_wasd' || source.type === 'keyboard_arrows') {
        // Also handle the gamepad portion for P1/P2 if their gamepad is connected
      }

      const gpIndex = source.gamepadIndex;
      const gp = gamepads[gpIndex];
      if (!gp) continue;

      const s = this._states[playerId];
      const btn = (i) => (gp.buttons[i]?.pressed ?? false);
      const axis = (i) => {
        const v = gp.axes[i] ?? 0;
        return Math.abs(v) > AXIS_DEADZONE ? v : 0;
      };

      // Movement
      const lx = axis(0);
      const ly = axis(1);
      s.moveAxis.x = lx;
      s.moveAxis.y = ly;
      s.up    = s.up    || btn(GP.DPAD_UP)    || ly < -AXIS_DEADZONE;
      s.down  = s.down  || btn(GP.DPAD_DOWN)  || ly >  AXIS_DEADZONE;
      s.left  = s.left  || btn(GP.DPAD_LEFT)  || lx < -AXIS_DEADZONE;
      s.right = s.right || btn(GP.DPAD_RIGHT) || lx >  AXIS_DEADZONE;

      // Aim (right stick)
      const rx = axis(2);
      const ry = axis(3);
      if (rx !== 0 || ry !== 0) {
        s.aimAxis.x = rx;
        s.aimAxis.y = ry;
      }

      // Action buttons
      s.confirm   = s.confirm   || btn(GP.A);
      s.cancel    = s.cancel    || btn(GP.B);
      s.inventory = s.inventory || btn(GP.Y);
      s.skipTurn  = s.skipTurn  || btn(GP.X);
      s.undoMove  = s.undoMove  || btn(GP.LB);
      s.submenu   = s.submenu   || btn(GP.SELECT);
      s.menuOpen  = s.menuOpen  || btn(GP.START);

      // D-Pad menu hotkeys
      s.questLog  = s.questLog  || btn(GP.DPAD_UP);
      s.charSheet = s.charSheet || btn(GP.DPAD_DOWN);
      s.compendium= s.compendium|| btn(GP.DPAD_LEFT);
      s.map       = s.map       || btn(GP.DPAD_RIGHT);

      // L3 + R3 simultaneously = debug console
      const l3 = btn(GP.L3);
      const r3 = btn(GP.R3);
      if (l3 && r3 && !this._states[playerId].debug) {
        s.debug   = true;
        s.anyKey  = true;
        EventBus.emit('input:debug', { playerId });
      } else if (!l3 || !r3) {
        s.debug = s.debug && false; // reset (keyboard debug may also set this)
      }

      if (s.confirm || s.cancel || s.menuOpen || btn(GP.A)) s.anyKey = true;
    }
  },

  // ─── Simulated actions (Playwright testing) ──────────────────────────────

  /**
   * Apply simulated actions set via simulateAction().
   * Simulated actions are treated as pressed for exactly one frame.
   */
  _applySimulated() {
    for (const [key, active] of this._simulated.entries()) {
      if (!active) {
        this._simulated.delete(key);
        continue;
      }

      const [playerIdStr, action] = key.split(':');
      const playerId = parseInt(playerIdStr, 10);
      if (playerId >= 0 && playerId < 4 && action) {
        if (action in this._states[playerId]) {
          this._states[playerId][action] = true;
          this._states[playerId].anyKey  = true;
        }
      }
      // Mark for removal next frame
      this._simulated.set(key, false);
    }
  },

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * Get the current input state for a specific player.
   * @param {number} playerId — 0 to 3
   * @returns {object} Input state object (do not mutate)
   */
  getState(playerId) {
    return this._states[playerId] ?? this._states[0];
  },

  /**
   * Get the previous frame's input state (for rising/falling edge detection).
   * @param {number} playerId — 0 to 3
   * @returns {object}
   */
  getPrevState(playerId) {
    return this._prevStates[playerId] ?? this._prevStates[0];
  },

  /**
   * Returns true if an action was just pressed this frame (rising edge).
   * @param {number} playerId
   * @param {string} action — key on the state object
   * @returns {boolean}
   */
  justPressed(playerId, action) {
    const cur  = this._states[playerId];
    const prev = this._prevStates[playerId];
    return cur?.[action] === true && prev?.[action] !== true;
  },

  /**
   * Assign an input source to a player slot.
   * @param {number} playerId
   * @param {{ type: 'keyboard_wasd'|'keyboard_arrows'|'gamepad', gamepadIndex?: number }} source
   */
  assignPlayer(playerId, source) {
    if (playerId < 0 || playerId > 3) {
      DebugLog.warn(`[input] assignPlayer: invalid playerId ${playerId}`);
      return;
    }
    this._playerSources[playerId] = source;
    DebugLog.info(`[input] Player ${playerId} assigned source: ${source.type} (gamepad ${source.gamepadIndex ?? 'n/a'})`);
  },

  /**
   * Simulate an input action for one frame.
   * Used by Playwright tests to drive game logic.
   *
   * @param {number} playerId — 0 to 3
   * @param {string} actionName — state key (e.g. 'confirm', 'up', 'inventory')
   */
  simulateAction(playerId, actionName) {
    const key = `${playerId}:${actionName}`;
    this._simulated.set(key, true);
    // Also immediately set on state for synchronous test checks
    if (this._states[playerId] && actionName in this._states[playerId]) {
      this._states[playerId][actionName] = true;
      this._states[playerId].anyKey = true;
    }
  },

  /**
   * Snapshot current states to prevStates. Call at end of each game frame.
   */
  endFrame() {
    for (let i = 0; i < 4; i++) {
      this._prevStates[i] = cloneState(this._states[i]);
    }
  },

  /**
   * Reset all states to unpressed. Useful when changing scenes.
   */
  resetAll() {
    for (let i = 0; i < 4; i++) {
      this._states[i]     = createPlayerState();
      this._prevStates[i] = createPlayerState();
    }
  },

  /**
   * Get the 0-based index of the player who most recently produced any input.
   * Scans all four player states for `anyKey === true` in priority order (P1 first).
   * Returns 0 (P1) as the fallback when no player is currently pressing anything.
   *
   * Scenes that need to know "which player is acting right now" should call this
   * during their update loop rather than hard-coding player 0.
   *
   * Note: InputManager indices are 0-based. CoopSystem player IDs are 1-based.
   * Convert with: coopPlayerId = getActivePlayer() + 1
   *
   * @returns {number} 0–3
   */
  getActivePlayer() {
    for (let i = 0; i < 4; i++) {
      if (this._states[i].anyKey) return i;
    }
    return 0;
  },
};

export default InputManager;
